import type { Messages } from './en';

// Simplified Chinese UI messages.
export const zh: Messages = {
  common: {
    cancel: '取消',
    read: '阅读',
    remove: '移除',
    save: '保存',
    saving: '保存中…',
    close: '关闭',
    goHome: '返回首页',
    error: (message: string) => `出错：${message}`,
  },

  shelf: {
    unableToLoadBooks: '无法加载书籍',
    signIn: '登录',
    logout: '退出登录',
    credits: '点数',
    buyCredits: '购买点数',
    help: '帮助',
    dockHint: '移动鼠标环视 · 滚轮缩放 · 点击打开',
    prevPage: '上一页书架',
    nextPage: '下一页书架',
    byAuthor: (author: string) => `作者：${author}`,
    removeBook: '移除书籍',
    confirmRemove: '确定要移除这本书吗？',
    translationFailed: '翻译失败',
    translating: '翻译中…',
    translatingProgress: (done: number, total: number, pct: number) =>
      `翻译中… ${done}/${total} 章（${pct}%）`,
    extractingGlossary: '正在提取术语表…',
    preparingArtwork: '正在生成封面与书脊…',
    safeToClose: '可以放心关闭本页——回来时书就准备好了。',
    completed: '✓ 已读完',
    percentRead: (pct: number) => `已读 ${pct}%`,
    notStarted: '未开始',
    uploadedTitle: '书籍已上传！',
    uploadedOriginalOnly: '正在生成封面与书脊——仅导入原文。',
    uploadedTranslating: '翻译进行中——可以放心关闭本页。',
  },

  closet: {
    loadingQuips: [
      '正在掸去书架上的灰尘…',
      '正在扶正书脊…',
      '正在点亮阅读灯…',
      '正在唤醒图书管理员…',
    ],
    bookInfo: '图书信息',
    formLabelNo: '表格 3',
    formLabelKind: '架签',
    labelField: '标签',
    labelPlaceholder: '如：科幻、待读…',
    labelHint: '这张纸条会夹在架子边缘',
    editShelfLabel: '编辑架签',
    titleField: '书名',
    authorField: '作者',
    statusField: '状态',
    progressField: '进度',
    translatingCount: (done: number, total: number) =>
      `翻译中… ${done}/${total}`,
    extractingGlossary: '正在提取术语表…',
    translating: '翻译中…',
    preparingArtwork: '正在生成封面与书脊…',
    completedStamp: '已读完',
    shelfTheme: '书架配色',
    shelfThemeNames: {
      walnut: '胡桃木',
      white: '白漆做旧',
      steel: '钢架',
    } as Record<string, string>,
  },

  upload: {
    dialogUpload: '上传书籍',
    dialogAddHere: '在此添加书籍',
    dialogConfirm: '确认上传书籍',
    form1No: '表格 1',
    form1Kind: '入藏',
    form2No: '表格 2',
    form2Kind: '编目',
    loginRequired: '请先登录再上传书籍',
    estimateFailed: '估算书籍费用失败',
    reEstimateFailed: '重新估算失败',
    uploading: '正在上传…',
    startingTranslation: '正在启动翻译…',
    analyzing: '正在分析书籍…',
    uploadFailed: '上传失败',
    networkError:
      '上传时网络出错，请重试。若持续失败，请附上时间和浏览器信息联系我们。',
    insufficientCredits: '点数不足',
    requiredField: '需要',
    availableField: '可用',
    needField: '尚缺',
    moreAmount: (amount: string) => `${amount}`,
    needMore: (amount: string) => `还差 ${amount}`,
    balanceField: '余额',
    afterField: '扣除后',
    creditsUnit: '点数',
    shelfField: '书架',
    selectedShelf: '所选书架',
    modeAria: '上传模式',
    translateThisBook: '翻译这本书',
    bilingualNote: '双语',
    importOriginalOnly: '仅导入原文',
    freeNote: '免费',
    languagesField: '语言',
    sourceLanguage: '源语言',
    targetLanguage: '目标语言',
    sameLanguageWarning: '源语言与目标语言相同——请修改其一',
    confirmTranslate: '确认并翻译',
    importWithoutTranslation: '不翻译，直接导入',
    affixHere: '将 epub 附于此处',
    languagesNextForm: '语言将在下一张表格中选择',
  },

  creditsModal: {
    title: '购买点数',
    dialogAria: '购买点数',
    balancePrefix: '当前余额：',
    creditsSuffix: '点数',
    packageUnit: '点数',
    info: '点数用于书籍翻译。',
  },

  reader: {
    loadingChapter: '正在加载章节…',
    prevChapter: '上一章',
    nextChapter: '下一章',
    menu: '菜单',
    tapToToggle: '点击段落切换翻译',
    note: '注',
    noteNotFound: '未找到对应的注释内容',
    viewInContext: '查看原文位置',
    returnToReading: '返回原位置',
    backToShelf: '返回书架',
    showTranslation: '显示译文',
    showOriginal: '显示原文',
    chapters: '章节',
    markAsRead: '标记为已读',
    markedRead: '✓ 已读',
    share: '分享',
    copyShareLink: '复制分享链接',
    copied: '✓ 已复制！',
    revokeShare: '取消分享',
    theme: '主题',
    themeAuto: '跟随系统',
    themeNames: {
      white: '纯白',
      paper: '纸白',
      sepia: '羊皮纸',
      green: '护眼绿',
      dark: '夜间',
    } as Record<string, string>,
    firstLineIndent: '首行缩进',
    typography: '排版',
    font: '字体',
    fontNames: {
      song: '宋体',
      kai: '楷体',
      sans: '黑体',
    } as Record<string, string>,
    fontSize: '字号',
    paragraphGap: '段间距',
    lineHeight: '行高',
    letterSpacing: '字间距',
    wordSpacing: '词间距',
    fontWeight: '字重',
    resetToDefault: '恢复默认',
    contents: '目录',
    search: '搜索',
    searchPlaceholder: '搜索原文与译文…',
    searching: '搜索中…',
    searchNoResults: '未找到匹配内容',
  },

  auth: {
    continueWithGoogle: '使用 Google 登录',
    emailPlaceholder: 'you@example.com',
    sendCode: '发送邮箱验证码',
    orEmail: '或使用邮箱登录',
    codeSentLabel: '验证码已发送至',
    codePlaceholder: '6 位验证码',
    verifyAndSignIn: '登录',
    changeEmail: '换个邮箱',
    sendCodeFailed: '验证码发送失败',
    verifyFailed: '登录失败',
  },

  book: {
    loadingContent: '正在加载书籍内容…',
    loadChapterFailed: '加载章节失败',
    createShareFailed: '创建分享链接失败',
    revokeShareFailed: '取消分享失败',
  },

  shared: {
    linkInvalid: '该分享链接已失效。',
    loadBookFailed: '加载书籍失败。',
    loadChapterFailed: '加载章节失败。',
    goToLibrary: '前往书架',
    loadingSharedBook: '正在加载分享的书籍…',
  },

  errors: {
    somethingWentWrong: '出错了',
    unexpectedError: '发生了意外错误，请重试。',
    tryAgain: '重试',
    fetchBooksFailed: '获取书籍失败',
    deleteBookFailed: '删除书籍失败',
    saveLabelFailed: '保存标签失败',
    moveBookFailed: '移动书籍失败',
  },

  pwa: {
    newVersion: '新版本可用',
    refresh: '刷新',
    updating: '更新中…',
  },

  languageNames: {
    zh: '中文',
    es: '西班牙语',
    fr: '法语',
    de: '德语',
    ja: '日语',
    ko: '韩语',
    ru: '俄语',
    en: '英语',
  } as Record<string, string>,
};
